import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '../lib/api';

const STATUSES = ['NEW', 'OPEN', 'IN_PROGRESS', 'WAITING_ON_CUSTOMER', 'ON_HOLD', 'RESOLVED', 'CLOSED', 'CANCELLED'];
const PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'CRITICAL'];

export default function TicketDetailPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const [reply, setReply] = useState('');
  const [internal, setInternal] = useState(false);

  const { data: ticket } = useQuery({
    queryKey: ['ticket', id],
    queryFn: async () => (await api.get(`/tickets/${id}`)).data,
  });
  const { data: agents = [] } = useQuery({
    queryKey: ['agents'],
    queryFn: async () => (await api.get('/agents')).data,
  });

  const patch = useMutation({
    mutationFn: async (changes: Record<string, unknown>) => api.patch(`/tickets/${id}`, changes),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ticket', id] }); toast.success('Updated'); },
  });

  const sendReply = useMutation({
    mutationFn: async () => api.post(`/tickets/${id}/replies`, { body: reply, isInternal: internal }),
    onSuccess: () => { setReply(''); setInternal(false); qc.invalidateQueries({ queryKey: ['ticket', id] }); toast.success('Reply sent'); },
  });

  if (!ticket) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="p-8 max-w-5xl">
      <Link to="/tickets" className="text-sm text-slate-500 hover:text-brand-600">← All tickets</Link>
      <div className="mt-2 flex items-start justify-between">
        <div>
          <div className="text-xs font-mono text-slate-500">{ticket.ticketNo}</div>
          <h1 className="text-2xl font-semibold text-slate-900">{ticket.subject}</h1>
          <div className="mt-1 text-xs text-slate-500">From {ticket.endCustomer?.name ?? ticket.contactName ?? '—'} · {ticket.channel}</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mt-6">
        <div className="col-span-2 space-y-4">
          {ticket.description && (
            <div className="card p-5">
              <div className="text-xs text-slate-500 uppercase mb-2">Description</div>
              <div className="whitespace-pre-wrap text-sm text-slate-800">{ticket.description}</div>
            </div>
          )}
          <div className="card p-5">
            <div className="text-xs text-slate-500 uppercase mb-3">Conversation</div>
            <div className="space-y-3">
              {ticket.replies.map((r: { id: string; body: string; isInternal: boolean; createdAt: string; agent?: { name: string } | null; endCustomer?: { name: string } | null }) => (
                <div key={r.id} className={'rounded-lg p-3 ' + (r.isInternal ? 'bg-amber-50 border border-amber-200' : r.agent ? 'bg-brand-50 border border-brand-100' : 'bg-slate-50 border border-slate-200')}>
                  <div className="text-xs text-slate-500 mb-1">
                    {r.isInternal && <span className="badge bg-amber-200 text-amber-900 mr-2">Internal</span>}
                    <strong className="text-slate-700">{r.agent?.name ?? r.endCustomer?.name ?? 'External'}</strong>
                    <span className="ml-2">{new Date(r.createdAt).toLocaleString()}</span>
                  </div>
                  <div className="whitespace-pre-wrap text-sm text-slate-800">{r.body}</div>
                </div>
              ))}
              {ticket.replies.length === 0 && <div className="text-sm text-slate-500 italic">No replies yet.</div>}
            </div>
          </div>
          <div className="card p-5">
            <textarea className="input min-h-[100px]" placeholder="Type your reply…" value={reply} onChange={(e) => setReply(e.target.value)} />
            <div className="mt-3 flex items-center justify-between">
              <label className="text-xs flex items-center gap-2 text-slate-600">
                <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} /> Internal note (only your team sees this)
              </label>
              <button className="btn-primary" disabled={!reply || sendReply.isPending} onClick={() => sendReply.mutate()}>
                {sendReply.isPending ? 'Sending…' : 'Send reply'}
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="card p-5">
            <div className="text-xs text-slate-500 uppercase mb-3">Properties</div>
            <div className="space-y-3">
              <div>
                <label className="label">Status</label>
                <select className="input" value={ticket.status} onChange={(e) => patch.mutate({ status: e.target.value })}>
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Priority</label>
                <select className="input" value={ticket.priority} onChange={(e) => patch.mutate({ priority: e.target.value })}>
                  {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Assignee</label>
                <select className="input" value={ticket.assigneeId ?? ''} onChange={(e) => patch.mutate({ assigneeId: e.target.value || null })}>
                  <option value="">— Unassigned</option>
                  {(agents as { id: string; name: string }[]).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div className="card p-5">
            <div className="text-xs text-slate-500 uppercase mb-2">SLA</div>
            <div className="text-sm space-y-1 text-slate-700">
              <div>First reply due: <strong>{ticket.firstReplyDueAt ? new Date(ticket.firstReplyDueAt).toLocaleString() : '—'}</strong></div>
              <div>Resolve due: <strong>{ticket.resolveDueAt ? new Date(ticket.resolveDueAt).toLocaleString() : '—'}</strong></div>
              <div>First reply at: <strong>{ticket.firstReplyAt ? new Date(ticket.firstReplyAt).toLocaleString() : '—'}</strong></div>
            </div>
          </div>
          <div className="card p-5">
            <div className="text-xs text-slate-500 uppercase mb-2">Activity</div>
            <ul className="text-xs text-slate-600 space-y-1">
              {ticket.activities.slice().reverse().slice(0, 12).map((a: { id: string; type: string; createdAt: string }) => (
                <li key={a.id}><span className="text-slate-400">{new Date(a.createdAt).toLocaleString()}</span> · {a.type}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
